import { useEffect, useState } from 'react';
import {
  CSGetentityUsersByEntityId,
  EntityUser,
  Task,
} from '@admiin-com/ds-graphql';
import {
  useCurrentEntityId,
  useSelectedEntity,
} from '../../../../hooks/useSelectedEntity/useSelectedEntity';
import { getFromS3Storage } from '@admiin-com/ds-amplify';
import { useTaskToName } from '../../../../hooks/useTaskToName/useTaskToName';
import { BillCreateFormData } from '../../../providers/TaskCreationProvider/TaskCreationProvider';
import { DocumentPreviewData } from '../../../../components/DocumentRenderer/DocumentRenderer';
import { defaultTaskFormValue } from '../../components/DocumentPreviewModal';
import {
  enumToCapitalizedString,
  frontDateFromBackendDate,
} from '@admiin-com/ds-common';
import {
  formatSettlementAmount,
  formatSettlementAmountDollars,
  getFrequencyPeriodDescription,
} from '../../../../utils/invoice';
import { gql, useQuery } from '@apollo/client';
import { useClientSelection } from '../../../hooks/useClientSelection/useClientSelection';
import { DateTime } from 'luxon';

type PreviewTaskDateValue = string | number | Date | null | undefined;

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
type PreviewRecord = Record<string, any>;

const isRecord = (value: unknown): value is PreviewRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const parsePreviewAddress = (
  value: unknown
): PreviewRecord | undefined => {
  if (isRecord(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const normalizePreviewParty = (
  value: unknown
): PreviewRecord | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const contact = isRecord(value.contact) ? value.contact : undefined;
  const normalizedAddress = parsePreviewAddress(value.address);

  return {
    ...value,
    ...(normalizedAddress ? { address: normalizedAddress } : {}),
    ...(value.firstName === undefined && contact?.firstName !== undefined
      ? { firstName: contact.firstName }
      : {}),
    ...(value.lastName === undefined && contact?.lastName !== undefined
      ? { lastName: contact.lastName }
      : {}),
  };
};

const formatPreviewDateValue = (
  value: PreviewTaskDateValue
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat('dd LLL yyyy');
  }

  if (typeof value === 'number') {
    return DateTime.fromMillis(value).toFormat('dd LLL yyyy');
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    if (ISO_DATE_REGEX.test(trimmed)) {
      return frontDateFromBackendDate(trimmed);
    }

    return trimmed;
  }

  return undefined;
};

const formatPreviewTask = (task: unknown): unknown => {
  if (!task || typeof task !== 'object') {
    return task;
  }

  const taskRecord = task as Record<string, any>;

  const formattedTask: Record<string, any> = {
    ...taskRecord,
    dueAt: formatPreviewDateValue(taskRecord.dueAt),
    lodgementAt: formatPreviewDateValue(taskRecord.lodgementAt),
    dueDate: formatPreviewDateValue(taskRecord.dueDate),
    dueOn: formatPreviewDateValue(taskRecord.dueOn),
    due: formatPreviewDateValue(taskRecord.due),
    paymentAt: formatPreviewDateValue(taskRecord.paymentAt),
    issuedAt: formatPreviewDateValue(taskRecord.issuedAt),
    automaticInvoicePaymentAt: formatPreviewDateValue(
      taskRecord.automaticInvoicePaymentAt
    ),
  };

  if (
    taskRecord.futureLinkedTask &&
    typeof taskRecord.futureLinkedTask === 'object'
  ) {
    formattedTask.futureLinkedTask = {
      ...taskRecord.futureLinkedTask,
      dueAt: formatPreviewDateValue(taskRecord.futureLinkedTask?.dueAt),
      dueDate: formatPreviewDateValue(taskRecord.futureLinkedTask?.dueDate),
      issuedAt: formatPreviewDateValue(taskRecord.futureLinkedTask?.issuedAt),
    };
  }

  return formattedTask;
};

const formatPreviewTasks = (tasks?: unknown[]): unknown[] | undefined => {
  if (!Array.isArray(tasks)) {
    return undefined;
  }

  return tasks.map((task) => formatPreviewTask(task));
};

const getCurrentYear = (): string => new Date().getFullYear().toString();

export const resolvePreviewYear = (...candidateYears: unknown[]): string => {
  for (const candidate of candidateYears) {
    if (typeof candidate !== 'string') {
      continue;
    }

    const trimmed = candidate.trim();
    if (trimmed) {
      return trimmed;
    }
  }

  return getCurrentYear();
};

export const useDocumentPreview = (
  selectedTask: Task | null,
  taskCreationContext?: BillCreateFormData
) => {
  const { entity } = useSelectedEntity();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const getTaskName = useTaskToName();
  const [previewData, setPreviewData] = useState<DocumentPreviewData | null>(
    null
  );
  const entityId = useCurrentEntityId();
  const { data: usersData } = useQuery(gql(CSGetentityUsersByEntityId), {
    variables: {
      entityId: entityId,
      limit: 1000,
    },
  });
  const { accountantUser } = useClientSelection();
  const partnerUser = usersData?.entityUsersByEntityId?.items?.find(
    (item: EntityUser) => item.userId === accountantUser?.partnerId
  );

  useEffect(() => {
    const fetchLogo = async () => {
      let url = null;
      try {
        if (entity?.logo?.key) {
          url = await getFromS3Storage({
            key: entity.logo.key,
            identityId: entity.logo.identityId,
            level: entity.logo.level,
          });
        }
      } catch (error) {
        console.error('Error getting logo URL:', error);
      }
      setLogoUrl(url);
    };

    fetchLogo();
  }, [entity]);

  useEffect(() => {
    const getPreviewValues = async () => {
      if (selectedTask) {
        const [{ data: to }, { data: partner }] = await Promise.all([
          getTaskName(selectedTask.toId, true),
          getTaskName(selectedTask.partnerId, true),
        ]);

        const from = normalizePreviewParty({
          ...entity,
          logoUrl,
        });
        const formattedTo = normalizePreviewParty(to);

        const period = getFrequencyPeriodDescription(
          selectedTask.paymentFrequency ?? '',
          selectedTask.dueAt ?? ''
        );

        const formattedTask = {
          ...selectedTask,
          settlementStatus: enumToCapitalizedString(
            selectedTask.settlementStatus
          ),
          invoiceStatus: enumToCapitalizedString(selectedTask.invoiceStatus),
          amount: formatSettlementAmount(
            selectedTask?.futureLinkedTask?.amount ?? selectedTask.amount ?? 0,
            selectedTask.settlementStatus
          ),
          dueAt: frontDateFromBackendDate(selectedTask.dueAt ?? ''),
          lodgementAt: frontDateFromBackendDate(selectedTask.lodgementAt ?? ''),
          issuedAt: frontDateFromBackendDate(selectedTask.issuedAt ?? ''),
          paymentAt: frontDateFromBackendDate(selectedTask.paymentAt ?? ''),
          period,
          // firstPaymentAt: frontDateFromBackendDate(
          //   selectedTask?.firstPaymentAt ?? ''
          // ),
          automaticInvoicePaymentAt: frontDateFromBackendDate(
            selectedTask.automaticInvoicePaymentAt ?? ''
          ),
          year: resolvePreviewYear(
            (selectedTask as unknown as { year?: string }).year,
            (taskCreationContext as unknown as { year?: string })?.year
          ),
        };

        const previewTasks = Array.isArray(taskCreationContext?.tasks)
          ? (taskCreationContext?.tasks as unknown[])
          : Array.isArray(
              (selectedTask as unknown as { tasks?: unknown[] })?.tasks
            )
          ? (selectedTask as unknown as { tasks?: unknown[] }).tasks ??
            undefined
          : undefined;

        const formattedPreviewTasks = formatPreviewTasks(previewTasks);

        setPreviewData({
          task: formattedTask,
          from,
          to: formattedTo,
          partner,
          tasks: formattedPreviewTasks,
          dates: {
            today: new Date().toLocaleDateString('en-GB'),
            year: resolvePreviewYear(
              formattedTask.year,
              (taskCreationContext as unknown as { year?: string })?.year
            ),
            month: new Date().toLocaleString('en-GB', {
              month: 'long',
            }),
          },
        });
      } else {
        const base = taskCreationContext ?? defaultTaskFormValue;
        const from = normalizePreviewParty({
          ...entity,
          logoUrl,
        });
        const formattedTo = normalizePreviewParty(base.to);

        const basePeriod = getFrequencyPeriodDescription(
          base.paymentFrequency ??
            taskCreationContext?.futureTaskInput?.paymentFrequency ??
            '',
          base.dueAt ?? taskCreationContext?.futureTaskInput?.dueAt ?? ''
        );

        const formattedBase = {
          ...base,
          settlementStatus: enumToCapitalizedString(base.settlementStatus),
          invoiceStatus: enumToCapitalizedString(base.invoiceStatus),
          amount: formatSettlementAmountDollars(
            parseFloat(
              base.amount ?? taskCreationContext?.futureTaskInput?.amount ?? '0'
            ),
            base.settlementStatus
          ),
          dueAt: frontDateFromBackendDate(base.dueAt ?? ''),
          lodgementAt: frontDateFromBackendDate(base.lodgementAt ?? ''),
          issuedAt: frontDateFromBackendDate(
            base.issuedAt ??
              taskCreationContext?.futureTaskInput?.issuedAt ??
              ''
          ),
          paymentAt: frontDateFromBackendDate(base.paymentAt ?? ''),
          firstPaymentAt: frontDateFromBackendDate(base.firstPaymentAt ?? ''),
          automaticInvoicePaymentAt: frontDateFromBackendDate(
            base.automaticInvoicePaymentAt ?? ''
          ),
          period: taskCreationContext?.period ?? basePeriod,
          year: resolvePreviewYear(
            (base as unknown as { year?: string }).year,
            (taskCreationContext as unknown as { year?: string })?.year
          ),
        };

        const previewTasks = Array.isArray(taskCreationContext?.tasks)
          ? (taskCreationContext?.tasks as unknown[])
          : Array.isArray((formattedBase as { tasks?: unknown[] }).tasks)
          ? (formattedBase as { tasks?: unknown[] }).tasks
          : undefined;

        const formattedPreviewTasks = formatPreviewTasks(previewTasks);

        setPreviewData({
          task: formattedBase,
          from,
          to: formattedTo,
          partner: partnerUser,
          tasks: formattedPreviewTasks,
          dates: {
            today: new Date().toLocaleDateString('en-GB'),
            year: resolvePreviewYear(
              formattedBase.year,
              (taskCreationContext as unknown as { year?: string })?.year
            ),
            month: new Date().toLocaleString('en-GB', {
              month: 'long',
            }),
          },
        } as DocumentPreviewData);
      }
    };

    getPreviewValues();
  }, [selectedTask, taskCreationContext, logoUrl, entity, getTaskName]);

  return previewData;
};
